
import { getSupabase } from "./supabaseClient";
import { debugLog } from "../utils/debugLogger";

export interface SubscriptionStatus {
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'incomplete';
  plan: 'monthly' | 'yearly';
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
}

export const subscriptionService = {
  
  /**
   * Fetches the subscription record via server API
   */
  async getSubscription(): Promise<SubscriptionStatus | null> {
    try {
      const { data: { session } } = await getSupabase().auth.getSession();
      if (!session) return null;

      const res = await fetch('/api/subscription', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!res.ok) throw new Error("API call failed");
      
      const data = await res.json();
      return data as SubscriptionStatus | null;
    } catch (e: any) {
      debugLog("SUB", "Failed to fetch subscription", e.message);
      return null;
    }
  },

  /**
   * Polls the API until a subscription is found or timeout reached.
   */
  async pollForSubscription(timeoutMs = 30000): Promise<SubscriptionStatus | null> {
    const start = Date.now();
    
    while (Date.now() - start < timeoutMs) {
      const sub = await this.getSubscription();
      if (sub && (sub.status === 'active' || sub.status === 'trialing')) {
        return sub;
      }
      await new Promise(r => setTimeout(r, 2000));
    }
    
    return null;
  },

  /**
   * Gets the portal URL
   */
  async getPortalUrl(): Promise<string> {
    try {
        const { data: { session } } = await getSupabase().auth.getSession();
        if (!session) return "https://polar.sh/portal";

        const res = await fetch('/api/polar/portal', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        return data.url;
    } catch (e) {
        return "https://polar.sh/portal";
    }
  },

  /**
   * Attempts to restore a subscription from Polar if local state is out of sync.
   */
  async restorePurchase(): Promise<{ success: boolean; message: string }> {
    try {
        const { data: { session } } = await getSupabase().auth.getSession();
        if (!session) throw new Error("Please log in first.");

        const res = await fetch('/api/polar/restore', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json'
            }
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to connect to server");

        return {
            success: data.success,
            message: data.message || (data.success ? "Subscription restored." : "No active subscription found.")
        };
    } catch (e: any) {
        console.error("Restore failed", e);
        return { success: false, message: e.message || "Unable to restore purchase." };
    }
  }
};
